import { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, CircularProgress, ClickAwayListener, InputAdornment, Paper, Popper, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { formatPhone } from '@functions/format-phone';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { resolve } from '@layouts/pages/Multichannel/components/channel-identity';
import { GlobalSearchResponse, SearchService } from '@services/search.service';
import { ELeadOutcome } from '@layouts/pages/CRM/kanban-board/services/responses/lead.response';

const OUTCOME_KEY: Partial<Record<ELeadOutcome, string>> = {
    [ELeadOutcome.Won]: 'desfecho.ganho',
    [ELeadOutcome.Lost]: 'desfecho.perdido',
};

const DEBOUNCE_MS = 300;
const MIN_LENGTH = 2;

interface Item {
    key: string;
    group: 'leads' | 'people' | 'conversations';
    title: string;
    detail?: string;
    icon: JSX.Element;
    to: string;
}

/// Busca do topo (lead, pessoa e conversa): Enter abre o primeiro resultado, setas navegam, Esc fecha.
export const GlobalSearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const anchorRef = useRef<HTMLDivElement>(null);
    const [term, setTerm] = useState('');
    const [result, setResult] = useState<GlobalSearchResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);

    useEffect(() => {
        const text = term.trim();

        if (text.length < MIN_LENGTH) {
            setResult(null);
            setLoading(false);
            return;
        }

        setLoading(true);

        // O termo mais recente manda: resposta atrasada de uma busca velha não sobrescreve a nova.
        let current = true;

        const timer = setTimeout(() => {
            new SearchService()
                .search(text)
                .then((res) => current && setResult(res.data.data ?? null))
                .catch(() => current && setResult(null))
                .finally(() => current && setLoading(false));
        }, DEBOUNCE_MS);

        return () => {
            current = false;
            clearTimeout(timer);
        };
    }, [term]);

    const items: Item[] = useMemo(() => {
        if (!result) return [];

        return [
            ...result.leads.map((lead) => ({
                key: `lead-${lead.id}`,
                group: 'leads' as const,
                title: lead.name,
                // Desfecho primeiro: lead encerrado achado pela busca parecia em andamento.
                detail: [
                    OUTCOME_KEY[lead.outcome] && t(OUTCOME_KEY[lead.outcome]!),
                    lead.columnName,
                    lead.statusName,
                    lead.phone && formatPhone(lead.phone),
                ].filter(Boolean).join(' · '),
                icon: <ViewKanbanOutlinedIcon sx={{ fontSize: 17, color: lead.statusColor || cc.accent }} />,
                to: `/crm/lead/${lead.id}`,
            })),
            ...result.people.map((person) => ({
                key: `person-${person.id}`,
                group: 'people' as const,
                title: person.name,
                detail: [person.companyName, person.phone && formatPhone(person.phone)].filter(Boolean).join(' · '),
                icon: <PersonOutlineIcon sx={{ fontSize: 17, color: cc.textSecondary }} />,
                to: `/crm/pessoas?busca=${encodeURIComponent(person.name)}`,
            })),
            ...result.conversations.map((conversation) => {
                const channel = resolve(conversation.channel);

                return {
                    key: `conversation-${conversation.id}`,
                    group: 'conversations' as const,
                    title: conversation.contactName
                        || (conversation.contactPhone ? formatPhone(conversation.contactPhone) : channel.label),
                    detail: conversation.lastMessage ?? undefined,
                    icon: <channel.Icon sx={{ fontSize: 17, color: channel.color }} />,
                    to: `/multicanal?conversa=${conversation.id}`,
                };
            }),
        ];
    }, [result, cc.accent, cc.textSecondary]);

    useEffect(() => setActive(0), [items]);

    const go = (item?: Item) => {
        if (!item) return;

        setOpen(false);
        setTerm('');
        navigate(item.to);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
        if (e.key === 'Enter') go(items[active]);
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => Math.min(items.length - 1, i + 1));
        }
        if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => Math.max(0, i - 1));
        }
    };

    const groupTitle: Record<Item['group'], string> = {
        leads: t('buscaGeral.leads'),
        people: t('buscaGeral.pessoas'),
        conversations: t('buscaGeral.conversas'),
    };

    const showPanel = open && term.trim().length >= MIN_LENGTH;

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box ref={anchorRef}>
                <TextField
                    size="small"
                    value={term}
                    placeholder={t('buscaGeral.placeholder')}
                    onChange={(e) => {
                        setTerm(e.target.value);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKeyDown}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                {loading
                                    ? <CircularProgress size={15} sx={{ color: cc.textMuted }} />
                                    : <SearchIcon fontSize="small" sx={{ color: cc.textMuted }} />}
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: { xs: 180, md: 280 },
                        '& .MuiOutlinedInput-root': {
                            height: 38,
                            bgcolor: cc.surfaceBg,
                            borderRadius: '9px',
                            color: cc.textPrimary,
                            '& fieldset': { borderColor: cc.border },
                        },
                    }}
                />

                <Popper
                    open={showPanel}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    sx={{ zIndex: 1300 }}
                >
                    <Paper
                        sx={{
                            mt: 0.75,
                            width: 380,
                            maxHeight: 440,
                            overflowY: 'auto',
                            bgcolor: cc.cardBg,
                            border: `1px solid ${cc.border}`,
                            borderRadius: '11px',
                            py: 0.75,
                        }}
                    >
                        {!loading && items.length === 0 && (
                            <Typography sx={{ fontSize: 13, color: cc.textMuted, px: 2, py: 1.25 }}>
                                {t('buscaGeral.nada')}
                            </Typography>
                        )}

                        {items.map((item, index) => (
                            <Box key={item.key}>
                                {(index === 0 || items[index - 1].group !== item.group) && (
                                    <Typography
                                        sx={{
                                            fontSize: 10.5,
                                            fontWeight: 700,
                                            letterSpacing: '.06em',
                                            textTransform: 'uppercase',
                                            color: cc.textMuted,
                                            px: 2,
                                            pt: index === 0 ? 0.5 : 1.25,
                                            pb: 0.5,
                                        }}
                                    >
                                        {groupTitle[item.group]}
                                    </Typography>
                                )}

                                <Box
                                    role="button"
                                    tabIndex={-1}
                                    onMouseEnter={() => setActive(index)}
                                    onClick={() => go(item)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.25,
                                        px: 2,
                                        py: 0.9,
                                        cursor: 'pointer',
                                        bgcolor: index === active ? cc.countBg : 'transparent',
                                    }}
                                >
                                    {item.icon}
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: cc.textPrimary }}>
                                            {item.title}
                                        </Typography>
                                        {item.detail && (
                                            <Typography noWrap sx={{ fontSize: 11.5, color: cc.textMuted }}>
                                                {item.detail}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        ))}
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};
